// Powerlete "must choose a size" guard. When the variant picker requires an explicit
// selection (no variant pre-selected), block Add to Bag until a size is chosen: scroll the
// picker into view, show an assertive message, highlight the options, and move focus to the
// first available size.
//
// IMPORTANT: the actual add-to-cart fires on the product form's SUBMIT, which Horizon
// dispatches from a single document-level *capture* delegate (see component.js). A
// form-level submit listener therefore runs AFTER Horizon's handler and is too late to stop
// the add. So we intercept the Add to Bag button's CLICK on the stable section root in the
// capture phase instead: cancelling the click prevents the form from submitting at all, so
// nothing is added. The sticky bar puppets the same button via .click(), so this covers it
// too. Listening on the section root (not the form) also survives variant re-renders (morph).
// No external dependencies.
class PwRequireSize extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('.shopify-section') || document;
    this.msg = this.querySelector('.pw-size-required__msg');
    /** Set when the guard blocks a sizeless Add to cart; consumed by the next variant:update. */
    this._pendingAdd = false;
    // Detail of the last TRUSTED click seen at this root, i.e. a real press - touch, mouse, or
    // keyboard - as opposed to a script-dispatched one. Needed because the guarded button is not
    // always the thing the shopper actually pressed: see _onClick below.
    this._lastTrustedClickDetail = null;
    if (!this.root.querySelector('variant-picker')) return;

    this._onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Record the input modality of every genuine press at this root, BEFORE the button check
      // below can return early. <sticky-add-to-cart> (sections/product-information.liquid) is
      // its own element, outside .buy-buttons-block, so a tap on the STICKY button never matches
      // the guarded-button selector and would otherwise be invisible to us entirely.
      if (event.isTrusted) this._lastTrustedClickDetail = event.detail;

      // Only guard the product form's Add to Bag button (in-flow or puppeted by the sticky bar).
      const button = target.closest(
        '.buy-buttons-block button[type="submit"], .buy-buttons-block [ref="addToCartButton"]'
      );
      if (!button) return;

      if (this._hasSelection()) {
        // Size chosen BEFORE pressing Add to cart: the ordinary path. Disarm, so a later size
        // change can never silently add again — the deferred add belongs only to the flow where
        // this guard actually blocked a press.
        this._pendingAdd = false;
        this._clear();
        return;
      }
      // No size chosen: cancel the click so the form never submits, then prompt.
      event.preventDefault();
      event.stopImmediatePropagation();

      // Which input pressed Add to cart, so _prompt() knows whether to move focus (see there).
      //
      // Cannot read this off `event` directly: sticky-add-to-cart.js's handleAddToCartClick()
      // (assets/sticky-add-to-cart.js) puppets a tap on THIS button via
      // `this.#targetAddToCartButton.click()` whenever a size is missing, so that a size chosen
      // via the sticky bar and the in-flow button share one guard. Per spec, HTMLElement.click()
      // always fires with isTrusted: false, so on the sticky-bar path `event` here is that
      // synthetic follow-up, not the shopper's actual tap - its own `detail` says nothing about
      // how the shopper pressed. Falling back to `_lastTrustedClickDetail`, set by the capture
      // listener above from the REAL click (the sticky button's tap, or a direct press on this
      // button), fixes that: it is always the genuine press, regardless of which button the
      // guard itself ends up running on.
      //
      // detail === 0 on a TRUSTED click means the press came from the keyboard (Enter/Space) or
      // assistive tech, not a real pointer/touch press.
      const pressDetail = event.isTrusted ? event.detail : this._lastTrustedClickDetail;
      this._prompt(pressDetail === 0);
    };
    // Capture phase so we run before the click triggers the native form submit.
    this.root.addEventListener('click', this._onClick, { capture: true });

    this._onChange = (event) => {
      if (event.target instanceof Element && event.target.closest('variant-picker')) this._clear();
    };
    this.root.addEventListener('change', this._onChange);

    // Deferred add-to-cart. When the guard blocks a sizeless Add to cart we remember that the
    // shopper already asked to add (see #pendingAdd in _prompt), so choosing a size finishes the
    // job they started rather than making them press Add to cart a second time.
    //
    // Fires on `variant:update`, NOT on `change`. Horizon resolves the variant asynchronously; on
    // `change` the form's hidden id input still holds the OLD variant, and product-form.js's
    // #getIntendedVariantId() falls back to exactly that input — so adding on `change` can add the
    // wrong size. `variant:update` is dispatched by variant-picker.js only once the new variant is
    // resolved, and product-form.js's own #onVariantUpdate has already written the new id by the
    // time this bubbles past. See memory horizon-submit-race-atc-block.
    this._onVariantUpdate = (event) => {
      if (!this._pendingAdd) return;

      // Mobile only, per Matt: on desktop the ordinary "press Add to cart again" flow stays.
      // Evaluated live (not cached at connect) so a rotate/resize is respected. 749px matches
      // the sticky Add to cart bar's own breakpoint (sections/product-information.liquid).
      if (!window.matchMedia('(max-width: 749px)').matches) {
        this._pendingAdd = false;
        return;
      }

      const detail = /** @type {CustomEvent} */ (event).detail;

      // Combined listing swapped in a DIFFERENT product. The shopper's "add" intent was for the
      // product they were looking at, so drop it rather than adding something they never saw.
      if (detail?.data?.newProduct) {
        this._pendingAdd = false;
        return;
      }

      // Multi-option product with another group still empty: stay armed so completing the LAST
      // group is what fires, not the first one.
      if (!this._hasSelection()) return;

      // One-shot. Disarm before dispatching so a re-render or a second variant:update in the same
      // tick can never add twice.
      this._pendingAdd = false;

      // Sold-out size: availability comes off the event payload, not the DOM, because the button's
      // disabled state is re-rendered asynchronously and may still be stale here. Let the normal
      // sold-out path (notify sheet) take over instead of adding.
      if (detail?.resource && detail.resource.available === false) return;

      const button = this.root.querySelector(
        '.buy-buttons-block button[type="submit"], .buy-buttons-block [ref="addToCartButton"]'
      );
      if (!(button instanceof HTMLElement)) return;
      if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

      // Re-enters _onClick, which now passes (selection satisfied) and lets the form submit.
      button.click();
    };
    this.root.addEventListener('variant:update', this._onVariantUpdate);
  }

  disconnectedCallback() {
    this.root?.removeEventListener('click', this._onClick, { capture: true });
    this.root?.removeEventListener('change', this._onChange);
    this.root?.removeEventListener('variant:update', this._onVariantUpdate);
  }

  /** The main variant picker, re-queried each time so we survive section re-renders. */
  _picker() {
    return this.root.querySelector('variant-picker');
  }

  /** Mobile sticky-bar mirror of the prompt. Re-queried (not cached) for the same reason as
   *  _picker(): the sticky bar re-renders on variant change. Absent on products without the
   *  sticky bar, and on desktop it is display:none, so a null here is normal. */
  _stickyMsg() {
    return this.root.querySelector('[data-pw-sticky-size-msg]');
  }

  /** Selection is satisfied when every option group (fieldset) has a checked radio.
   *  Products with no multi-value option (single/default variant, or a dropdown style)
   *  have no fieldsets, so there is nothing to choose and the guard never blocks. */
  _hasSelection() {
    const picker = this._picker();
    if (!picker) return true;
    const fieldsets = picker.querySelectorAll('fieldset');
    if (fieldsets.length === 0) return true;
    return Array.from(fieldsets).every((fs) => fs.querySelector('input[type="radio"]:checked'));
  }

  /**
   * @param {boolean} [fromKeyboard] - True when the blocked press came from the keyboard or
   *   assistive tech rather than a pointer/touch press.
   */
  _prompt(fromKeyboard) {
    const picker = this._picker();
    if (!picker) return;

    // The shopper has asked to add. Remember it so choosing a size completes the add.
    this._pendingAdd = true;
    if (this.msg) this.msg.hidden = false;
    const sticky = this._stickyMsg();
    if (sticky) sticky.hidden = false;
    picker.classList.add('pw-size-required-highlight');

    // a11y: tie each unsatisfied option group to the message and mark it invalid, so the
    // requirement is programmatic (not the role="alert"/colour cue alone). Only flag groups
    // with no checked radio, so an already-chosen group on a multi-option product is left alone.
    if (this.msg && this.msg.id) {
      picker.querySelectorAll('fieldset').forEach((fs) => {
        if (fs.querySelector('input[type="radio"]:checked')) return;
        fs.setAttribute('aria-describedby', this.msg.id);
        fs.setAttribute('aria-invalid', 'true');
      });
    }

    // Jump straight to the picker on mobile rather than animating.
    //
    // The prompt renders immediately under the sticky Add to cart bar, so a smooth scroll left a
    // transit window where the shopper could see "Please choose your size" but not the sizes, with
    // the page sliding underneath — from the top of a long PDP that ran about a second. An instant
    // move lands them on the highlighted options with no in-between state. Desktop keeps the
    // smooth scroll: there is no sticky bar there, the picker is usually already near the
    // viewport, and the shorter travel never showed the problem.
    // Reduced motion still forces 'auto' at every width, as before.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const instant = reduce || window.matchMedia('(max-width: 749px)').matches;

    if (instant) {
      // Explicit window.scrollTo rather than scrollIntoView for the instant path.
      // scrollIntoView delegates to the nearest scrollable ANCESTOR, which on this PDP does not
      // reliably move the page (measured: scrollIntoView left window.scrollY at 0 while a direct
      // window.scrollTo(600) worked on the same element). Computing the target ourselves keeps the
      // mobile jump deterministic, and is the behaviour we actually want: centre the picker in the
      // viewport, clamped so short pages cannot overscroll.
      const rect = picker.getBoundingClientRect();
      const centred = window.scrollY + rect.top - (window.innerHeight - rect.height) / 2;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({ top: Math.max(0, Math.min(centred, maxScroll)), behavior: 'instant' });
    } else {
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Move focus onto the first available size ONLY for keyboard / assistive-tech presses, where
    // it saves a long tab journey to the control that needs attention.
    //
    // Never after a tap. Safari applies :focus-visible to programmatic focus far more readily than
    // Chrome, so the focused radio picked up the black focus ring from
    // `.variant-option__button-label:has(:focus-visible)` and READ AS AN ALREADY-CHOSEN SIZE -
    // directly contradicting the "Please choose your size" message right beneath it. The radio is
    // only focused, never checked, so the outline was purely misleading. Reported by Matt on
    // iOS, 2026-08-28. Touch users lose nothing: the message carries role="alert" and the option
    // groups are wired up via aria-describedby / aria-invalid, so the requirement is still
    // announced and still programmatic.
    if (!fromKeyboard) return;

    const firstAvailable = picker.querySelector(
      'input[type="radio"]:not([aria-disabled="true"]):not([disabled])'
    );
    if (firstAvailable) firstAvailable.focus({ preventScroll: true });
  }

  _clear() {
    if (this.msg) this.msg.hidden = true;
    const sticky = this._stickyMsg();
    if (sticky) sticky.hidden = true;
    const picker = this._picker();
    picker?.classList.remove('pw-size-required-highlight');
    picker?.querySelectorAll('fieldset').forEach((fs) => {
      fs.removeAttribute('aria-describedby');
      fs.removeAttribute('aria-invalid');
    });
  }
}

if (!customElements.get('pw-require-size')) {
  customElements.define('pw-require-size', PwRequireSize);
}
